// RUTA: src/components/fitness/TempoDisplay.tsx
//
// Display amigable del tempo de un ejercicio para el panel del
// CLIENTE. Reemplaza el código críptico "4-0-1-1" + iconos por un
// listado vertical con etiquetas en español claro.
//
// Reglas de display:
//
//   1. Si las 3 primeras fases son 0 y solo hay aguante → ISOMÉTRICO
//      (plancha, hold, etc.). Mostramos 'Aguanta la posición X seg'.
//
//   2. Si todas las fases son 0 o no están definidas → no mostrar
//      nada (return null).
//
//   3. Si hay tempo "normal", mostrar SOLO las fases con valor > 0:
//        ↓ Bajada       4 seg
//        ⏸ Pausa abajo  1 seg
//        ↑ Subida       1 seg
//        ⏸ Aguanta arr. 1 seg
//
// Filosofía: el cliente normal entiende sin saber notación de
// levantamiento. El coach sigue viendo el código técnico en su panel
// (no usa este componente — usa el render histórico).

interface TempoDisplayProps {
  /** Tempo eccéntrico (bajada) en segundos */
  eccentrica?: number | null;
  /** Pausa abajo en segundos */
  pausaAbajo?: number | null;
  /** Tempo concéntrico (subida) en segundos */
  concentrica?: number | null;
  /** Pausa arriba en segundos */
  pausaArriba?: number | null;
  /** Código original (ej. "4-0-1-1" o legacy "30s"). Se usa como
   *  fallback para parsear ejercicios isométricos legacy donde solo
   *  está el campo `isometry_seconds` poblado. NO se muestra al
   *  cliente como código. */
  code?: string | null;
}

/** Parsea formato legacy "30s" o "60s" → segundos. Devuelve null si
 *  no matchea. */
function parseLegacyIsometricCode(code: string | null | undefined): number | null {
  if (!code) return null;
  const m = code.trim().match(/^(\d+)\s*s$/i);
  if (!m) return null;
  const n = Number(m[1]);
  return n > 0 ? n : null;
}

interface Phase {
  icon: string;
  label: string;
  seconds: number;
}

export function TempoDisplay({
  eccentrica,
  pausaAbajo,
  concentrica,
  pausaArriba,
  code,
}: TempoDisplayProps) {
  const ec  = Number(eccentrica)   || 0;
  const pa  = Number(pausaAbajo)   || 0;
  const co  = Number(concentrica)  || 0;
  const par = Number(pausaArriba)  || 0;

  // Caso especial: ejercicios isométricos legacy con solo
  // `isometry_seconds` (planchas, wall sit). El código viene como
  // "30s" / "60s". Si los campos numéricos están todos en 0 pero
  // hay un legacy code parseable, lo mostramos como isométrico.
  const legacyIsometricSeconds = parseLegacyIsometricCode(code);
  if (ec === 0 && pa === 0 && co === 0 && par === 0 && legacyIsometricSeconds) {
    return (
      <div className="rounded-lg border-2 border-secondary bg-secondary/40 p-3 mt-2">
        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
          <span>⏱️</span> Isométrico
        </p>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-base">⏸</span>
          <span className="font-bold flex-1">Aguanta la posición</span>
          <span className="font-black text-accent">{legacyIsometricSeconds} seg</span>
        </div>
      </div>
    );
  }

  // Caso 1: todo en 0 → no mostrar nada
  if (ec === 0 && pa === 0 && co === 0 && par === 0) return null;

  // Caso 2: solo hay aguante arriba (o solo pausa abajo) sin bajada
  // ni subida → es ejercicio isométrico (plancha, wall sit, etc.)
  // Mostrar como "Aguanta X seg" — formato más natural.
  const isIsometrico = ec === 0 && co === 0 && (pa > 0 || par > 0);
  if (isIsometrico) {
    const seconds = Math.max(pa, par);
    return (
      <div className="rounded-lg border-2 border-secondary bg-secondary/40 p-3 mt-2">
        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center justify-center gap-1.5">
          <span>⏱️</span> Isométrico
        </p>
        <div className="flex items-center gap-3 text-sm px-1">
          <span className="text-base w-5 text-center shrink-0">⏸</span>
          <span className="font-bold flex-1 min-w-0">Aguanta la posición</span>
          <span className="font-black text-accent shrink-0">{seconds} seg</span>
        </div>
      </div>
    );
  }

  // Caso 3: tempo normal — mostrar solo fases con valor > 0
  const phases: Phase[] = [];
  if (ec  > 0) phases.push({ icon: '↓', label: 'Bajada',          seconds: ec });
  if (pa  > 0) phases.push({ icon: '⏸', label: 'Pausa abajo',     seconds: pa });
  if (co  > 0) phases.push({ icon: '↑', label: 'Subida',          seconds: co });
  if (par > 0) phases.push({ icon: '⏸', label: 'Aguanta arriba',  seconds: par });

  if (phases.length === 0) return null;

  return (
    <div className="rounded-lg border-2 border-secondary bg-secondary/40 p-3 mt-2">
      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center justify-center gap-1.5">
        <span>⏱️</span> Cómo hacer cada repetición
      </p>
      <ul className="space-y-1.5 px-1">
        {phases.map((phase, i) => (
          <li key={i} className="flex items-center gap-3 text-sm">
            <span className="text-base w-5 text-center shrink-0" aria-hidden="true">
              {phase.icon}
            </span>
            <span className="font-bold flex-1">{phase.label}</span>
            <span className="font-black text-accent shrink-0">
              {phase.seconds} seg
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
